<script setup lang="ts">
  /**
   * Ops Layout
   *
   * Layout for internal operations site (ops.claimshieldpro.com).
   * Features:
   * - Collapsible sidebar with sections
   * - Nested navigation items with expand/collapse
   * - Header with admin tools
   * - Role-based navigation
   * - "View As" feature for admins to see UI as other users
   */
  import { computed, ref, watch } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useAuthStore } from '../../stores/auth'
  import { useUIStore } from '../../stores/ui'
  import {
    activeNavRoute,
    useOpsNav,
    type NavItem,
    type NavSection,
  } from '../../composables/useOpsNav'
  import PhoneWidget from '../PhoneWidget.vue'
  import PushNotificationToggle from '../PushNotificationToggle.vue'
  import IosInstallNudge from '../IosInstallNudge.vue'
  import NotificationBell from '../NotificationBell.vue'
  import RecentCarriersMenu from '../RecentCarriersMenu.vue'
  import { api } from '../../services/api'
  import type { User } from '../../types/auth'
  import logoHorizontalReverse from '../../assets/logos/claimshield-horizontal-reverse.png'
  import logoVertical from '../../assets/logos/claimshield-vertical.png'

  // Global search
  const globalSearch = ref('')
  const globalSearchFocused = ref(false)
  const globalSearchResults = ref<Array<{ type: string; id: string; label: string; sub: string; client_id?: string | null }>>([])
  const searchLoading = ref(false)
  const searchFailed = ref(false)
  let searchDebounce: ReturnType<typeof setTimeout> | null = null
  /**
   * Bumped on every keystroke; a response applies only if it is still the
   * latest. Before, a slow answer for "smi" could land after the answer for
   * "smith" and replace it, and the previous query's rows stayed clickable
   * while the new one was typed and debounced (ops audit 2026-10-02, shell #3).
   */
  let searchSeq = 0

  watch(globalSearch, (val) => {
    if (searchDebounce) clearTimeout(searchDebounce)
    const seq = ++searchSeq
    // Clear at once: rows for the old query must not sit under the new one.
    globalSearchResults.value = []
    searchFailed.value = false
    if (!val || val.trim().length < 2) {
      searchLoading.value = false
      return
    }
    searchLoading.value = true
    searchDebounce = setTimeout(async () => {
      try {
        const res = await api.get('/v1/portal/search', { params: { q: val.trim() } })
        if (seq !== searchSeq) return
        globalSearchResults.value = res.data?.data ?? []
      } catch {
        if (seq !== searchSeq) return
        // A failed search is not "No results" (ops audit 2026-10-02, shell #7).
        searchFailed.value = true
      } finally {
        if (seq === searchSeq) searchLoading.value = false
      }
    }, 250)
  })

  function delayedBlurSearch() {
    setTimeout(() => { globalSearchFocused.value = false }, 200)
  }

  function handleSearchSelect(result: { type: string; id: string; client_id?: string | null }) {
    globalSearch.value = ''
    globalSearchFocused.value = false
    globalSearchResults.value = []

    if (result.type === 'Client') {
      router.push({ path: '/', query: { client: result.id } })
    } else if (result.type === 'Driver' || result.type === 'Applicant') {
      const query: Record<string, string> = {}
      if (result.client_id) query.client = result.client_id
      query.highlight = result.id
      router.push({ path: '/', query })
    }
  }

  const router = useRouter()
  const route = useRoute()
  const authStore = useAuthStore()
  const uiStore = useUIStore()

  // Navigation — single source of truth shared with the ⌘K command palette.
  const { navSections } = useOpsNav()

  // Platform-aware hint for the command-palette launcher chip.
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || '')
  const paletteHint = isMac ? '⌘K' : 'Ctrl K'

  // Track expanded nav items
  const expandedItems = ref<Set<string>>(new Set())

  // --- Sidebar nav filter (sketch 056-C: in-sidebar search filters navigation) ---
  const navFilter = ref('')

  const filteredNavSections = computed((): NavSection[] => {
    const q = navFilter.value.trim().toLowerCase()
    if (!q) return navSections.value
    return navSections.value
      .map((section) => ({
        ...section,
        items: section.items
          .map((item): NavItem | null => {
            if (item.children) {
              if (item.label.toLowerCase().includes(q)) return item
              const children = item.children.filter((c) => c.label.toLowerCase().includes(q))
              return children.length ? { ...item, children } : null
            }
            return item.label.toLowerCase().includes(q) ? item : null
          })
          .filter((i): i is NavItem => i !== null),
      }))
      .filter((s) => s.items.length > 0)
  })

  // View As feature state
  const staffUsers = ref<User[]>([])
  const loadingStaff = ref(false)
  const viewAsMenuRef = ref()

  // Fetch staff users for View As dropdown (admin only)
  async function fetchStaffUsers() {
    if (!authStore.isTrueAdmin) return

    loadingStaff.value = true
    try {
      const response = await api.get<{
        success: boolean
        data: { users: User[]; total: number }
      }>('/v1/admin/users', {
        params: { page_size: 50, role: 'staff' }
      })

      if (response.data.success) {
        // Include all users (staff + admin) for view as options
        staffUsers.value = response.data.data.users.filter(
          (u: User) => u.id !== authStore.user?.id // Exclude self
        )
      }
    } catch (err) {
      console.error('Failed to fetch staff users:', err)
    } finally {
      loadingStaff.value = false
    }
  }

  // Handle View As selection
  function handleViewAs(targetUser: User) {
    authStore.setViewingAs(targetUser)
    viewAsMenuRef.value?.hide()
  }

  // Load staff users when user becomes available (handles async auth initialization)
  watch(
    () => authStore.user,
    (newUser) => {
      console.log('[OpsLayout] User changed:', newUser?.email, 'role:', newUser?.role)
      if (newUser && newUser.role === 'admin' && staffUsers.value.length === 0) {
        console.log('[OpsLayout] Admin detected, fetching staff users...')
        fetchStaffUsers()
      }
    },
    { immediate: true }
  )

  const userMenuItems = [
    { label: 'Profile', icon: 'pi pi-user', command: () => router.push('/profile') },
    { separator: true },
    { label: 'Logout', icon: 'pi pi-sign-out', command: () => handleLogout() },
  ]

  // Theme controls
  const themeIcon = computed(() => {
    switch (uiStore.theme) {
      case 'dark':
        return 'pi pi-moon'
      case 'light':
        return 'pi pi-sun'
      default:
        return 'pi pi-desktop' // system
    }
  })

  const themeTooltip = computed(() => {
    switch (uiStore.theme) {
      case 'dark':
        return 'Dark mode (click for light)'
      case 'light':
        return 'Light mode (click for system)'
      default:
        return 'System theme (click for dark)'
    }
  })

  function cycleTheme() {
    const order: ('light' | 'dark' | 'system')[] = ['light', 'dark', 'system']
    const currentIndex = order.indexOf(uiStore.theme)
    const nextIndex = (currentIndex + 1) % order.length
    uiStore.setTheme(order[nextIndex]!)
  }

  /**
   * Which sidebar row is lit: exactly one, the most specific match.
   *
   * Prefix matching keeps `Vehicle Search` lit on a vehicle-history page;
   * `NavItem.exact` keeps `Open by DOT` (`/atlas`) off its siblings (AST3-2).
   * Lighting every match also lit the overview rows above a screen, so the
   * layout now asks `activeNavRoute` for the single longest match.
   */
  const activeRoute = computed(() =>
    activeNavRoute(route.path, navSections.value.flatMap((section) => section.items)),
  )
  const isNavActive = (itemRoute: string) => itemRoute === activeRoute.value

  // Check if any child route is active
  const hasActiveChild = (item: NavItem): boolean => {
    if (!item.children) return false
    return item.children.some((child) => child.route && isNavActive(child.route))
  }

  // Toggle expanded state for nested items
  const toggleExpanded = (label: string) => {
    if (expandedItems.value.has(label)) {
      expandedItems.value.delete(label)
    } else {
      expandedItems.value.add(label)
    }
  }

  // Check if item is expanded (or has active child). While filtering, always
  // expand so matched children are visible.
  const isExpanded = (item: NavItem): boolean => {
    if (navFilter.value.trim()) return true
    return expandedItems.value.has(item.label) || hasActiveChild(item)
  }

  async function handleLogout() {
    const logoutUrl = await authStore.logout()
    // Identity sign-in: the store has already sent the browser to the sign-in
    // server's sign-out. Without that URL, say so rather than offer /login,
    // which would sign straight back in on the surviving sign-in session.
    if (authStore.mode === 'oidc') {
      if (!logoutUrl) router.push({ path: '/login', query: { signed_out: '1' } })
      return
    }
    router.push('/login')
  }
</script>

<template>
  <!-- Fullscreen mode: route-level (presentations) OR embed mode (?embed=1
       set by the Zoho CRM widget — hides chrome so the view fits in a CRM
       home-page iframe). -->
  <template v-if="route.meta.fullscreen || route.query.embed === '1'">
    <RouterView />
  </template>

  <!-- Normal layout -->
  <div v-else class="flex h-screen bg-secondary-50 print:h-auto print:bg-white">
    <!-- iOS install nudge (push unlock banner) -->
    <IosInstallNudge />

    <!-- Sidebar -->
    <aside
      :class="[
        'ops-sb fixed inset-y-0 left-0 z-30 flex flex-col transition-all duration-300 print:hidden',
        uiStore.sidebarCollapsed ? 'w-16' : 'w-64',
        uiStore.sidebarMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
      ]"
    >
      <!-- Logo -->
      <div class="flex h-16 items-center justify-center border-b border-white/10 px-4">
        <template v-if="!uiStore.sidebarCollapsed">
          <img :src="logoHorizontalReverse" alt="ClaimShield" class="h-8 w-auto" />
          <span class="ml-2 rounded bg-primary-500 px-1.5 py-0.5 text-xs font-medium text-white">
            OPS
          </span>
        </template>
        <template v-else>
          <img :src="logoVertical" alt="ClaimShield" class="h-9 w-auto" />
        </template>
      </div>

      <!-- Sidebar nav search (sketch 056-C) — filters navigation; the header
           global search finds records and ⌘K adds actions. -->
      <div v-if="!uiStore.sidebarCollapsed" class="px-3 pt-3">
        <div class="sb-search">
          <i class="pi pi-search text-xs" />
          <input
            v-model="navFilter"
            type="text"
            placeholder="Search"
            class="sb-search-input w-full bg-transparent text-sm outline-none"
          />
          <button v-if="navFilter" type="button" class="sb-search-clear" @click="navFilter = ''">
            <i class="pi pi-times text-xs" />
          </button>
        </div>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 overflow-y-auto p-3">
        <div
          v-for="section in filteredNavSections"
          :key="section.title"
          :class="uiStore.sidebarCollapsed ? 'mb-4' : 'nav-card'"
        >
          <h3 v-if="!uiStore.sidebarCollapsed && section.title" class="nav-card-title">
            {{ section.title }}
          </h3>
          <div class="space-y-0.5">
            <template v-for="item in section.items" :key="item.label">
              <!-- Item with children (expandable) -->
              <template v-if="item.children">
                <button
                  :class="[
                    'nav-row w-full',
                    hasActiveChild(item) ? 'nav-row-open' : '',
                    uiStore.sidebarCollapsed ? 'justify-center !px-0' : '',
                  ]"
                  @click="toggleExpanded(item.label)"
                  v-tooltip.right="uiStore.sidebarCollapsed ? item.label : undefined"
                >
                  <i :class="[item.icon, 'nav-icon']" />
                  <span v-if="!uiStore.sidebarCollapsed" class="flex-1 text-left">{{ item.label }}</span>
                  <i
                    v-if="!uiStore.sidebarCollapsed"
                    :class="[
                      'pi text-xs transition-transform duration-200',
                      isExpanded(item) ? 'pi-chevron-down' : 'pi-chevron-right',
                    ]"
                  />
                </button>
                <!-- Children -->
                <div
                  v-if="!uiStore.sidebarCollapsed && isExpanded(item)"
                  class="ml-5 mt-0.5 space-y-0.5 border-l border-white/10 pl-2.5"
                >
                  <RouterLink
                    v-for="child in item.children"
                    :key="child.route"
                    :to="child.route!"
                    :class="[
                      'nav-row',
                      isNavActive(child.route!) ? 'nav-row-active' : '',
                    ]"
                  >
                    <i :class="[child.icon, 'w-4 text-center text-sm']" />
                    <span>{{ child.label }}</span>
                  </RouterLink>
                </div>
              </template>

              <!-- Regular item (no children) -->
              <RouterLink
                v-else
                :to="item.route!"
                :class="[
                  'nav-row',
                  isNavActive(item.route!) ? 'nav-row-active' : '',
                  uiStore.sidebarCollapsed ? 'justify-center !px-0' : '',
                ]"
                v-tooltip.right="uiStore.sidebarCollapsed ? item.label : undefined"
              >
                <i :class="[item.icon, 'nav-icon']" />
                <span v-if="!uiStore.sidebarCollapsed">{{ item.label }}</span>
                <!-- Count pill. NO-OP for every existing nav item: `badge` is
                     undefined unless a store published one via setNavBadge, and
                     both 0 and null render nothing (0 is "counted, nothing to
                     do"; null is "not counted yet" — neither is a call to
                     action). Hidden when the rail is collapsed, where the label
                     it qualifies is hidden too. -->
                <span
                  v-if="!uiStore.sidebarCollapsed && typeof item.badge === 'number' && item.badge > 0"
                  class="nav-badge"
                  data-test="nav-badge"
                  >{{ item.badge }}</span
                >
              </RouterLink>
            </template>
          </div>
        </div>
        <div v-if="navFilter && filteredNavSections.length === 0" class="sb-empty">
          No matches for "{{ navFilter }}"
        </div>
      </nav>

      <!-- Account card (sketch 056-C) — opens the user menu (Profile / Logout),
           which used to live in the header. -->
      <PMenu ref="userMenu" :model="userMenuItems" popup />
      <button
        type="button"
        :class="['sb-account self-stretch text-left', uiStore.sidebarCollapsed ? 'justify-center !px-1' : '']"
        v-tooltip.right="uiStore.sidebarCollapsed ? authStore.userDisplayName : undefined"
        @click="($refs.userMenu as any).toggle($event)"
      >
        <span class="sb-avatar">{{ authStore.userInitials }}</span>
        <div v-if="!uiStore.sidebarCollapsed" class="min-w-0 flex-1">
          <div class="truncate text-[13px] font-semibold text-white">{{ authStore.userDisplayName }}</div>
          <div class="truncate text-[11px] capitalize text-[#8a92bd]">{{ authStore.user?.role }}</div>
        </div>
        <i
          v-if="!uiStore.sidebarCollapsed"
          class="pi pi-ellipsis-v text-xs text-[#8a92bd]"
        />
      </button>

      <!-- Collapse toggle -->
      <button
        class="hidden h-12 w-full box-border items-center justify-center border-t border-white/10 text-[#8a92bd] hover:bg-white/5 hover:text-white lg:flex"
        @click="uiStore.toggleSidebar"
      >
        <i :class="uiStore.sidebarCollapsed ? 'pi pi-angle-right' : 'pi pi-angle-left'" />
      </button>
    </aside>

    <!-- Mobile sidebar overlay -->
    <div
      v-if="uiStore.sidebarMobileOpen"
      class="fixed inset-0 z-20 bg-black/50 lg:hidden"
      @click="uiStore.closeMobileSidebar"
    />

    <!-- Main content area -->
    <div
      :class="[
        'flex min-w-0 flex-1 flex-col transition-all duration-300 print:ml-0',
        uiStore.sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64',
      ]"
    >
      <!-- Header -->
      <header class="relative sticky top-0 z-10 flex h-16 items-center justify-between bg-white px-4 lg:px-6 print:hidden">
        <!-- Mobile menu button -->
        <button
          class="rounded-lg p-2 text-secondary-600 hover:bg-secondary-100 lg:hidden"
          @click="uiStore.toggleMobileSidebar"
        >
          <i class="pi pi-bars text-xl" />
        </button>

        <!-- Breadcrumb / Page title -->
        <div class="hidden lg:block">
          <h1 class="text-lg font-semibold text-secondary-900">
            {{ route.meta.title?.toString().split(' | ')[0] || 'Dashboard' }}
          </h1>
        </div>

        <!-- Right side -->
        <div class="flex items-center gap-3">
          <!-- Global Search -->
          <div class="relative hidden md:block">
            <i class="pi pi-search pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-sm text-secondary-400" />
            <input
              v-model="globalSearch"
              type="text"
              placeholder="Search all..."
              class="h-9 w-56 rounded-lg border border-secondary-200 bg-secondary-50 pl-9 pr-14 text-sm text-secondary-700 placeholder-secondary-400 outline-none transition-colors focus:border-primary-400 focus:bg-white focus:ring-1 focus:ring-primary-400"
              @focus="globalSearchFocused = true"
              @blur="delayedBlurSearch"
            />
            <!-- Command-palette launcher hint (⌘K). The header box searches
                 records; the palette adds pages + actions. -->
            <button
              type="button"
              class="absolute right-1.5 top-1/2 hidden -translate-y-1/2 items-center rounded border border-secondary-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-secondary-400 transition-colors hover:border-secondary-300 hover:text-secondary-600 lg:inline-flex"
              aria-label="Open command palette"
              v-tooltip.bottom="'Command palette'"
              @mousedown.prevent="uiStore.toggleCommandPalette()"
            >
              {{ paletteHint }}
            </button>
            <div
              v-if="globalSearchFocused && globalSearch.trim().length >= 2"
              class="absolute right-0 top-full z-50 mt-1 w-80 max-h-80 overflow-y-auto rounded-lg border border-secondary-200 bg-white shadow-xl"
            >
              <div v-if="searchLoading" class="px-4 py-3 text-center text-sm text-secondary-500">
                <i class="pi pi-spin pi-spinner mr-1" /> Searching...
              </div>
              <div v-else-if="searchFailed" role="alert" class="px-4 py-3 text-sm text-red-700">
                Search didn't go through. Keep typing or try again.
              </div>
              <div v-else-if="globalSearchResults.length === 0" class="px-4 py-3 text-sm text-secondary-500">
                No results for "{{ globalSearch }}"
              </div>
              <button
                v-for="r in globalSearchResults"
                :key="r.type + r.id"
                class="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-secondary-50 transition-colors"
                @mousedown.prevent="handleSearchSelect(r)"
              >
                <span
                  class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
                  :class="{
                    'bg-blue-100 text-blue-700': r.type === 'Driver',
                    'bg-amber-100 text-amber-700': r.type === 'Applicant',
                    'bg-green-100 text-green-700': r.type === 'Client',
                  }"
                >
                  {{ r.type[0] }}
                </span>
                <div class="min-w-0 flex-1">
                  <div class="truncate text-sm font-medium text-secondary-800">{{ r.label }}</div>
                  <div v-if="r.sub" class="truncate text-xs text-secondary-400">{{ r.sub }}</div>
                </div>
                <span class="flex-shrink-0 text-[10px] font-medium uppercase text-secondary-400">{{ r.type }}</span>
              </button>
            </div>
          </div>

          <!-- View As dropdown (admin only) -->
          <template v-if="authStore.isTrueAdmin">
            <PPopover ref="viewAsMenuRef">
              <div class="w-64 max-h-80 overflow-y-auto">
                <div class="px-3 py-2 border-b border-secondary-200 sticky top-0 bg-white">
                  <div class="text-xs font-semibold text-secondary-500 uppercase">View As</div>
                </div>

                <!-- Loading state -->
                <div v-if="loadingStaff" class="px-3 py-4 text-center text-secondary-500">
                  <i class="pi pi-spin pi-spinner mr-2" />
                  Loading users...
                </div>

                <!-- Staff user list -->
                <div v-else-if="staffUsers.length > 0">
                  <button
                    v-for="staffUser in staffUsers"
                    :key="staffUser.id"
                    class="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-secondary-100"
                    @click="handleViewAs(staffUser)"
                  >
                    <PAvatar
                      :label="(staffUser.first_name?.[0] || '') + (staffUser.last_name?.[0] || '')"
                      size="small"
                      shape="circle"
                      class="bg-secondary-200 text-secondary-700"
                    />
                    <div class="flex-1">
                      <div class="text-sm font-medium text-secondary-800">
                        {{ staffUser.first_name }} {{ staffUser.last_name }}
                      </div>
                      <div class="text-xs text-secondary-500 capitalize">{{ staffUser.role }}</div>
                    </div>
                  </button>
                </div>

                <!-- Empty state -->
                <div v-else class="px-3 py-4 text-center text-secondary-500 text-sm">
                  No other users found
                </div>

                <!-- Back to own view button -->
                <div v-if="authStore.isViewingAs" class="px-3 py-2 border-t border-secondary-200">
                  <button
                    class="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded"
                    @click="authStore.clearViewingAs(); viewAsMenuRef?.hide()"
                  >
                    <i class="pi pi-times" />
                    Back to my view
                  </button>
                </div>
              </div>
            </PPopover>
            <button
              :class="[
                'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                authStore.isViewingAs
                  ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                  : 'text-secondary-600 hover:bg-secondary-100'
              ]"
              @click="viewAsMenuRef?.toggle($event)"
              v-tooltip.bottom="'View as another user'"
            >
              <i class="pi pi-eye" />
              <span class="hidden md:inline">
                {{ authStore.isViewingAs ? `Viewing as ${authStore.userDisplayName}` : 'View As' }}
              </span>
            </button>
          </template>

          <!-- Recent carriers (Task G5 — CAB "Recent CAB Reports" parity) -->
          <RecentCarriersMenu />

          <!-- Theme Toggle -->
          <button
            class="theme-toggle"
            @click="cycleTheme"
            v-tooltip.bottom="themeTooltip"
          >
            <i :class="themeIcon" class="text-lg" />
          </button>

          <!-- Notifications -->
          <PushNotificationToggle />
          <NotificationBell />
        </div>

        <!-- ClaimShield signature line — purple→gold gradient hairline running
             the full header width (sidebar edge → right). Replaces the plain
             grey divider; the brand seam also used atop the report-nudge card. -->
        <div class="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-primary-800 via-primary-600 to-accent-500"></div>
      </header>

      <!-- Page content -->
      <main class="flex-1 overflow-y-auto">
        <!-- Routes opt into full-bleed with `meta: { wide: true }` (wide-canvas
             pages like the Conversion Cockpit kanban, where the 1800px cap
             hides columns on ultrawide displays). Everything else keeps the cap. -->
        <div :class="route.meta.wide ? 'page-container-wide' : 'page-container'">
          <RouterView />
        </div>
      </main>
    </div>

    <!-- Phone Widget (staff+ only) -->
    <PhoneWidget v-if="authStore.isStaff" :show-button="false" />
  </div>
</template>

<style scoped>
/* Sidebar — sketch 056-C "inset section cards" (domain-tinted chips dropped
   2026-07-13, Ahmed: too cartoonish for the team — icons stay monochrome).
   Winning sketch: lab/sketches/056-ops-sidebar-macos/ (variant C). Navy-indigo
   dark base (Ahmed 2026-07-13: blue shade to match the content's indigo accents). */
.ops-sb {
  /* Original sidebar base (secondary-900 slate) — Ahmed 2026-07-13: keep the
     pre-restyle color; the navy-indigo tint read too purple. */
  background: theme('colors.secondary.900');
  color: #d3d7e8;
}

/* In-sidebar nav search */
.sb-search {
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: #7780ad;
}
.sb-search input {
  color: #edeff8;
}
.sb-search input::placeholder {
  color: #7780ad;
}
/* The inner input must not carry the app-wide input focus-ring (a theme('colors.primary.500')
   box-shadow) — inside the dark rounded search box it reads as a stray bright
   rectangle (same fix as the ⌘K palette input). The container border brighten
   below is the focus cue instead. */
.sb-search-input:focus,
.sb-search-input:focus-visible {
  outline: none;
  box-shadow: none;
}
.sb-search:focus-within {
  border-color: rgba(255, 255, 255, 0.22);
}
.sb-search-clear {
  color: #7780ad;
  transition: color 0.12s;
}
.sb-search-clear:hover {
  color: #fff;
}
.sb-empty {
  padding: 16px 12px;
  text-align: center;
  font-size: 12px;
  color: #7780ad;
}

/* Inset section cards */
.nav-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 6px;
}
.nav-card + .nav-card {
  margin-top: 10px;
}
/* Section headers are the portal's primary wayfinding — they answer "what else
   is in here?" on every screen. #6d76a8 measured 3.6:1 against the nav card,
   under the 4.5:1 WCAG AA floor for 12px text; #a9b1dc measures 7.5:1. The gold
   tick anchors each section to the brand (on dark, gold leads — knowledge/brand/
   tokens.md) and stays far inside the <=10% gold ceiling at 10x2px per section.
   Tracking follows the canon's label scale (+0.08em, was 0.05em). */
.nav-card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 4px 8px 6px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #a9b1dc;
}
.nav-card-title::before {
  content: '';
  flex: none;
  width: 10px;
  height: 2px;
  border-radius: 999px;
  background: var(--cs-gold);
}

/* Nav rows + selection pill */
.nav-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 8px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #d3d7e8;
  transition: background 0.12s, color 0.12s;
}
.nav-row:hover {
  background: rgba(255, 255, 255, 0.07);
  color: #fff;
}
.nav-row-open {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}
.nav-row-active,
.nav-row-active:hover {
  background: linear-gradient(180deg, var(--cs-purple-pill), var(--cs-purple-light));
  color: #fff;
}

/* Monochrome nav icons — fixed width so labels align like the old sidebar */
.nav-icon {
  width: 23px;
  flex-shrink: 0;
  text-align: center;
  font-size: 15px;
  color: #9aa3cc;
}
.nav-row:hover .nav-icon,
.nav-row-open .nav-icon,
.nav-row-active .nav-icon {
  color: #fff;
}

/* Nav count pill. Only rendered when a store published a count > 0, so this
   rule adds nothing to the existing nav. `margin-left:auto` pushes it to the
   right edge without touching .nav-row, which every other item shares. */
.nav-badge {
  margin-left: auto;
  min-width: 20px;
  padding: 1px 6px;
  border-radius: 9999px;
  background: var(--cs-purple-pill, #6b4faf);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

/* Account card */
.sb-account {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 12px 10px;
  padding: 8px 10px;
  border-radius: 10px;
  transition: background 0.15s;
}
.sb-account:hover {
  background: rgba(255, 255, 255, 0.06);
}
.sb-avatar {
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 12px;
  color: #fff;
  background: linear-gradient(135deg, var(--cs-gold), theme('colors.accent.700'));
}

/* Sidebar nav scrollbar — match the dark sidebar so the right edge reads as
   one continuous line top-to-bottom. The default browser scrollbar gutter on
   a dark background creates a visible light strip that pinches the OPS badge
   at the top and makes the bottom collapse button (no scrollbar) look 12-15px
   wider than the nav rows. Both symptoms are fixed by styling this scrollbar. */
nav.flex-1 {
  scrollbar-width: thin;
  scrollbar-color: #2d3563 transparent;
}
nav.flex-1::-webkit-scrollbar {
  width: 6px;
}
nav.flex-1::-webkit-scrollbar-track {
  background: transparent;
}
nav.flex-1::-webkit-scrollbar-thumb {
  background: #2d3563;
  border-radius: 3px;
}
nav.flex-1::-webkit-scrollbar-thumb:hover {
  background: #3e478a;
}
</style>
